import { Slot } from '@radix-ui/react-slot';
import { forwardRef } from 'react';
import classNames from '@/lib/classNames';
import { type VariantProps, cva } from 'class-variance-authority';

const buttonVariants = cva(
    'font-medium text-center inline-flex items-center justify-center rounded-full whitespace-nowrap transition duration-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:pointer-events-none disabled:opacity-50',
    {
        variants: {
            variant: {
                outline: 'border border-transparent border',
                fill: 'border-primary bg-primary text-white',
                text: 'border-transparent bg-transparent text-primary underline hover:bg-transparent hover:text-primary',
                ghost: 'hover:bg-primary/50 hover:text-white',
                white: 'border border-white text-primary bg-white hover:bg-primary',
            },
            colour: {
                white: 'border-white text-white hover:border-primary hover:text-primary',
                primary: 'border-primary text-primary hover:bg-primary hover:text-white',
                shell: 'border-shell-25 text-primary hover:bg-shell-25 hover:text-white',
            },
            size: {
                sm: 'px-4 py-2 uppercase',
                lg: 'px-6 py-3 2xl:text-lg',
                xl: 'px-7 py-4 text-md leading-120',
            },
            uppercase: {
                true: 'uppercase tracking-[0.03375rem]',
                false: '',
            },
        },
        compoundVariants: [
            {
                variant: 'fill',
                colour: 'primary',
                class: 'text-white hover:bg-primary', // override and make sure we've got white text
            },
            {
                variant: 'fill',
                colour: 'white',
                class: 'bg-white text-primary hover:bg-white', // override and make sure we've got white text
            },
            {
                variant: 'outline',
                colour: 'white',
                class: 'hover:bg-white hover:text-primary', // override and make sure we've got white text
            },
            {
                variant: 'outline',
                size: 'lg',
                class: 'border-2',
            },
            {
                variant: 'ghost',
                colour: 'white',
                class: 'hover:bg-white hover:text-primary', // override and make sure we've got white text
            },
        ],
        defaultVariants: {
            variant: 'outline',
            colour: 'primary',
            size: 'sm',
            uppercase: false,
        },
    }
);

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>, VariantProps<typeof buttonVariants> {
    asChild?: boolean;
}

/**
 * Button component
 */
const Button = forwardRef<HTMLButtonElement, ButtonProps>(({ className, variant, colour, size, uppercase, asChild = false, ...props }, ref) => {
    const Comp = asChild ? Slot : 'button';
    return <Comp className={classNames(buttonVariants({ variant, colour, size, uppercase, className }))} ref={ref} {...props} />;
});

Button.displayName = 'Button';

export { Button, buttonVariants };
export default Button;
